import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Lock, Unlock, Clock, TrendingUp, Coins, Wallet, 
  ChevronRight, Info, Loader2, CheckCircle, AlertCircle,
  Gift, Zap, Shield, Star
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModal';
import { useSolana } from '../context/WalletContext';
import { useToken } from '../context/TokenContext';
import { Layout } from '../components/Layout';
import { LoginRequired } from '../components/LoginRequired';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { SEOHead } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color palette for dynamic plans
const colorPalette = [
  { color: 'from-amber-500 to-amber-600', glowColor: 'rgba(6, 182, 212, 0.3)', badge: 'Flexible' },
  { color: 'from-purple-500 to-purple-600', glowColor: 'rgba(168, 85, 247, 0.3)', badge: 'Popular' },
  { color: 'from-amber-500 to-orange-500', glowColor: 'rgba(245, 158, 11, 0.3)', badge: 'Best APY' },
  { color: 'from-green-500 to-emerald-600', glowColor: 'rgba(34, 197, 94, 0.3)', badge: 'New' },
  { color: 'from-pink-500 to-rose-600', glowColor: 'rgba(236, 72, 153, 0.3)', badge: 'Special' },
  { color: 'from-blue-500 to-indigo-600', glowColor: 'rgba(59, 130, 246, 0.3)', badge: 'Premium' },
];

// Icons for different duration ranges
const getIconForDuration = (duration) => {
  if (duration <= 7) return Clock;
  if (duration <= 30) return Shield;
  if (duration <= 90) return Star;
  return Zap;
};

// Get badge based on APY
const getBadgeForPlan = (apy, index, total) => {
  if (apy >= 30) return 'Best APY';
  if (index === Math.floor(total / 2)) return 'Popular';
  if (index === 0) return 'Flexible';
  return 'Special';
};

// Helper to convert backend plans to frontend format
const formatPlans = (backendPlans) => {
  if (!backendPlans) return [];
  
  const entries = Object.entries(backendPlans)
    .filter(([_, plan]) => plan.enabled !== false)
    .sort((a, b) => a[1].duration - b[1].duration);
  
  return entries.map(([id, plan], index) => {
    const colorStyle = colorPalette[index % colorPalette.length];
    const IconComponent = getIconForDuration(plan.duration);
    
    return {
      id,
      duration: plan.duration,
      durationLabel: plan.name || `${plan.duration} Days`,
      apy: plan.apy,
      minAmount: plan.min_amount,
      icon: IconComponent,
      color: colorStyle.color,
      glowColor: colorStyle.glowColor,
      badge: getBadgeForPlan(plan.apy, index, entries.length),
      popular: index === Math.floor(entries.length / 2),
      // On-chain fields
      pool_id: plan.pool_id,
      reward_rate: plan.reward_rate,
      lock_period: plan.lock_period,
    };
  });
};

export const Staking = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token, refreshUser, isAuthenticated } = useAuth();
  const { openLogin } = useLoginModal();
  const { publicKey, connected, signTransaction } = useSolana();
  const { getTokenName, getTokenSymbol, getTokenIcon } = useToken();
  
  // Coming Soon state - set to true to enable coming soon overlay
  const isComingSoon = true;
  
  const [loading, setLoading] = useState(true);
  const [stakingStats, setStakingStats] = useState(null);
  const [myStakes, setMyStakes] = useState([]);
  const [stakingSettings, setStakingSettings] = useState(null);
  const [stakingPlans, setStakingPlans] = useState([]);
  
  // Staking Dialog State
  const [showStakeDialog, setShowStakeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [staking, setStaking] = useState(false);
  const [stakeResult, setStakeResult] = useState(null);
  
  // Unstake Dialog State
  const [showUnstakeDialog, setShowUnstakeDialog] = useState(false);
  const [selectedStake, setSelectedStake] = useState(null);
  const [unstaking, setUnstaking] = useState(false);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchStakingData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  // Show login required if not authenticated
  if (!isAuthenticated) {
    return <LoginRequired feature={t('nav.staking', { defaultValue: 'Staking' })} />;
  }

  const fetchStakingData = async () => {
    try {
      setLoading(true);
      const [statsRes, stakesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/staking/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/staking/my-stakes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/staking/settings`),
      ]);
      
      setStakingStats(statsRes.data);
      setMyStakes(stakesRes.data);
      setStakingSettings(settingsRes.data);
      setStakingPlans(formatPlans(settingsRes.data?.plans));
    } catch (error) {
      console.error('Error fetching staking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStake = async () => {
    if (!selectedPlan || !stakeAmount || !connected || !publicKey) {
      toast.error(t('auth.connectWallet'));
      return;
    }

    const amount = parseFloat(stakeAmount);
    if (amount < selectedPlan.minAmount) {
      toast.error(`${t('staking.min')}: ${selectedPlan.minAmount} ${getTokenSymbol()}`);
      return;
    }

    if (amount > (user?.tickets || 0)) {
      toast.error(t('wallet.insufficientBalance'));
      return;
    }

    try {
      setStaking(true);
      
      // Request staking transaction from backend
      const response = await axios.post(
        `${API}/staking/stake`,
        {
          plan_id: selectedPlan.id,
          amount: amount,
          wallet_address: publicKey.toBase58(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.requires_signature && response.data.transaction) {
        // Sign transaction with wallet
        const { Transaction } = await import('@solana/web3.js');
        const txBuffer = Buffer.from(response.data.transaction, 'base64');
        const transaction = Transaction.from(txBuffer);
        
        const signedTx = await signTransaction(transaction);
        const signedTxBase64 = Buffer.from(signedTx.serialize()).toString('base64');
        
        // Submit signed transaction
        const submitRes = await axios.post(
          `${API}/staking/stake/submit`,
          {
            stake_id: response.data.stake_id,
            signed_transaction: signedTxBase64,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setStakeResult({
          success: true,
          amount: amount,
          plan: selectedPlan,
          signature: submitRes.data.signature,
          unlock_date: submitRes.data.unlock_date,
        });
      } else {
        // Off-chain staking (fallback)
        setStakeResult({
          success: true,
          amount: amount,
          plan: selectedPlan,
          unlock_date: response.data.unlock_date,
        });
      }

      await refreshUser();
      await fetchStakingData();
      toast.success(t('staking.stakingSuccess'));
    } catch (error) {
      console.error('Staking error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setStaking(false);
    }
  };

  const handleUnstake = async () => {
    if (!selectedStake || !connected || !publicKey) {
      toast.error(t('auth.connectWallet'));
      return;
    }

    const now = new Date();
    const unlockDate = new Date(selectedStake.unlock_date);
    if (now < unlockDate) {
      toast.error(t('staking.locked'));
      return;
    }

    try {
      setUnstaking(true);
      
      const response = await axios.post(
        `${API}/staking/unstake`,
        {
          stake_id: selectedStake.id,
          wallet_address: publicKey.toBase58(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.requires_signature && response.data.transaction) {
        const { Transaction } = await import('@solana/web3.js');
        const txBuffer = Buffer.from(response.data.transaction, 'base64');
        const transaction = Transaction.from(txBuffer);
        
        const signedTx = await signTransaction(transaction);
        const signedTxBase64 = Buffer.from(signedTx.serialize()).toString('base64');
        
        await axios.post(
          `${API}/staking/unstake/submit`,
          {
            stake_id: selectedStake.id,
            signed_transaction: signedTxBase64,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      await refreshUser();
      await fetchStakingData();
      toast.success(t('staking.unstakingSuccess'));
      setShowUnstakeDialog(false);
      setSelectedStake(null);
    } catch (error) {
      console.error('Unstaking error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setUnstaking(false);
    }
  };

  const closeStakeDialog = () => {
    setShowStakeDialog(false);
    setSelectedPlan(null);
    setStakeAmount('');
    setStakeResult(null);
  };

  const calculateReward = (amount, apy, days) => {
    return (amount * (apy / 100) * (days / 365)).toFixed(2);
  };

  const getTimeRemaining = (unlockDate) => {
    const now = new Date();
    const unlock = new Date(unlockDate);
    const diff = unlock - now;
    
    if (diff <= 0) return t('staking.readyToUnstake');
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h ${t('staking.remaining')}`;
    return `${hours}h ${t('staking.remaining')}`;
  };

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center p-4">
          <div className="text-center">
            <Lock className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('staking.loginRequired')}</h2>
            <p className="text-zinc-400 mb-4">{t('staking.connectWalletAccess')}</p>
            <Button
              onClick={() => openLogin()}
              className="bg-gradient-to-r from-amber-600 to-amber-700 text-white"
            >
              {t('auth.connectWallet')}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead page="staking" />
      <div className="min-h-screen bg-[#FFFDF7] pb-20 relative">
        {/* Coming Soon Overlay */}
        {isComingSoon && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center p-8 bg-gradient-to-br from-amber-900/80 to-[#0c2235]/90 border border-amber-500/30 rounded-2xl shadow-2xl shadow-amber-500/20 max-w-sm mx-4"
            >
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2 font-['Chakra_Petch'] uppercase tracking-wider">
                {t('common.comingSoon', { defaultValue: 'Coming Soon' })}
              </h2>
              <p className="text-zinc-400 text-sm mb-6">
                {t('staking.comingSoonDesc', { defaultValue: 'On-chain staking feature is under development. Stay tuned!' })}
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6"
              >
                {t('common.backToHome', { defaultValue: 'Back to Home' })}
              </Button>
            </motion.div>
          </div>
        )}
        
        {/* Main Content - Blurred when Coming Soon */}
        <div className={isComingSoon ? 'filter blur-sm pointer-events-none select-none' : ''}>
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#0c2235]/95 backdrop-blur-lg border-b border-amber-900/30 px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-white font-['Chakra_Petch'] uppercase tracking-wider">
                {t('staking.title')}
              </h1>
              <p className="text-zinc-400 text-xs">{t('staking.subtitle')}</p>
            </div>
            <div className="flex items-center gap-2 bg-[#0a1d30] px-3 py-2 rounded-xl border border-amber-800/30">
              {getTokenIcon() ? (
                <img src={getTokenIcon()} alt={getTokenSymbol()} className="w-5 h-5 rounded-full" />
              ) : (
                <Coins className="w-5 h-5 text-amber-400" />
              )}
              <span className="text-white font-bold">{user?.tickets || 0}</span>
              <span className="text-amber-400 text-sm">{getTokenSymbol()}</span>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gradient-to-br from-amber-900/30 to-amber-950/30 border border-amber-800/30 rounded-xl p-3">
              <div className="flex items-center gap-1 mb-1">
                <Lock className="w-3 h-3 text-amber-400" />
                <span className="text-zinc-400 text-xs">{t('staking.staked')}</span>
              </div>
              <p className="text-lg font-bold text-white font-['Chakra_Petch']">
                {stakingStats?.total_staked || 0}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-900/30 to-amber-950/30 border border-yellow-800/30 rounded-xl p-3">
              <div className="flex items-center gap-1 mb-1">
                <Zap className="w-3 h-3 text-yellow-400" />
                <span className="text-zinc-400 text-xs">{t('staking.accrued')}</span>
              </div>
              <p className="text-lg font-bold text-yellow-400 font-['Chakra_Petch']">
                +{(stakingStats?.total_accrued || 0).toFixed(4)}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-green-900/30 to-emerald-950/30 border border-green-800/30 rounded-xl p-3">
              <div className="flex items-center gap-1 mb-1">
                <Gift className="w-3 h-3 text-green-400" />
                <span className="text-zinc-400 text-xs">{t('staking.claimed')}</span>
              </div>
              <p className="text-lg font-bold text-green-400 font-['Chakra_Petch']">
                {stakingStats?.total_claimed || 0}
              </p>
            </div>
          </div>

          {/* Staking Plans */}
          <div>
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              {t('staking.stakingPlans')}
            </h2>
            
            <div className="space-y-3">
              {stakingPlans.map((plan) => {
                const PlanIcon = plan.icon;
                return (
                  <motion.div
                    key={plan.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedPlan(plan);
                      setShowStakeDialog(true);
                    }}
                    className={`relative overflow-hidden rounded-xl border ${
                      plan.popular ? 'border-purple-500/50' : 'border-amber-800/30'
                    } bg-[#0a1d30] cursor-pointer transition-all hover:border-amber-500/50`}
                    style={{ boxShadow: plan.popular ? `0 0 20px ${plan.glowColor}` : undefined }}
                  >
                    {plan.popular && (
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                        {plan.badge}
                      </div>
                    )}
                    
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
                            <PlanIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-bold">{plan.durationLabel}</p>
                            <p className="text-zinc-400 text-xs">{t('staking.lockPeriod')}</p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                            {plan.apy}%
                          </p>
                          <p className="text-zinc-400 text-xs">{t('staking.apy')}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-amber-800/20 flex items-center justify-between">
                        <span className="text-zinc-400 text-xs">
                          {t('staking.min')}: {plan.minAmount} {getTokenSymbol()}
                        </span>
                        <div className="flex items-center gap-1 text-amber-400 text-sm">
                          <span>{t('staking.stakeNow')}</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* My Stakes */}
          <div>
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-400" />
              {t('staking.myStakes')}
            </h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              </div>
            ) : myStakes.length === 0 ? (
              <div className="bg-[#0a1d30] border border-amber-800/30 rounded-xl p-6 text-center">
                <Lock className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-400">{t('staking.noActiveStakes')}</p>
                <p className="text-zinc-500 text-sm">{t('staking.startStaking')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myStakes.map((stake) => {
                  // Find matching plan from stakingPlans or create default style
                  const matchingPlan = stakingPlans.find(p => p.id === stake.plan_id);
                  const planColor = matchingPlan?.color || 'from-amber-500 to-amber-600';
                  const isUnlocked = stake.can_unstake || new Date() >= new Date(stake.unlock_date);
                  const accruedReward = stake.accrued_reward || 0;
                  const estimatedTotalReward = stake.estimated_total_reward || calculateReward(stake.amount, stake.apy, stake.duration_days);
                  
                  return (
                    <div
                      key={stake.id}
                      className={`bg-[#0a1d30] border ${isUnlocked ? 'border-green-500/50' : 'border-amber-800/30'} rounded-xl p-4`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${planColor} flex items-center justify-center`}>
                            {isUnlocked ? (
                              <Unlock className="w-4 h-4 text-white" />
                            ) : (
                              <Lock className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div>
                            <p className="text-white font-bold">{stake.amount} {getTokenSymbol()}</p>
                            <p className="text-zinc-400 text-xs">{stake.plan_name || stake.plan_id} @ {stake.apy}% APY</p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-green-400 font-bold">+{accruedReward.toFixed(4)}</p>
                          <p className="text-zinc-400 text-xs">{t('staking.accrued')} / {estimatedTotalReward.toFixed(2)} {t('staking.total')}</p>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mb-3">
                        <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-amber-500 to-green-500 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(100, (accruedReward / estimatedTotalReward) * 100)}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${isUnlocked ? 'text-green-400' : 'text-zinc-400'}`}>
                          {isUnlocked ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> {t('staking.readyToUnstake')}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {getTimeRemaining(stake.unlock_date)}
                            </span>
                          )}
                        </span>
                        
                        {stake.status === 'active' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedStake(stake);
                              setShowUnstakeDialog(true);
                            }}
                            disabled={!isUnlocked}
                            className={`text-white text-xs h-8 ${
                              isUnlocked 
                                ? 'bg-gradient-to-r from-green-600 to-emerald-600' 
                                : 'bg-zinc-700 cursor-not-allowed'
                            }`}
                          >
                            {isUnlocked ? t('staking.unstake') : t('staking.locked')}
                          </Button>
                        )}
                        
                        {stake.status === 'completed' && (
                          <span className="text-xs text-amber-400 font-medium">{t('staking.completed')}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Stake Dialog */}
        <Dialog open={showStakeDialog} onOpenChange={closeStakeDialog}>
          <DialogContent className="bg-[#0a1d30] border-amber-800/50 max-w-sm p-0 overflow-hidden">
            <AnimatePresence mode="wait">
              {stakeResult ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-6 text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{t('staking.stakingSuccess')}</h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    {stakeResult.amount} {getTokenSymbol()} {t('staking.stakingSuccessDesc')} {stakeResult.plan.durationLabel}
                  </p>
                  <div className="bg-[#133554] rounded-xl p-3 mb-4">
                    <p className="text-zinc-400 text-xs">{t('staking.estReward')}</p>
                    <p className="text-green-400 font-bold text-lg">
                      +{calculateReward(stakeResult.amount, stakeResult.plan.apy, stakeResult.plan.duration)} {getTokenSymbol()}
                    </p>
                  </div>
                  <Button onClick={closeStakeDialog} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                    {t('staking.done')}
                  </Button>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  {/* Header */}
                  <div className="p-6 border-b border-amber-800/30 text-center">
                    {selectedPlan && (
                      <>
                        <div className={`w-16 h-16 mx-auto mb-3 rounded-xl bg-gradient-to-br ${selectedPlan.color} flex items-center justify-center`}>
                          <selectedPlan.icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-lg font-bold text-white">{selectedPlan.durationLabel} Staking</h3>
                        <p className="text-green-400 font-bold">{selectedPlan.apy}% APY</p>
                      </>
                    )}
                  </div>

                  {/* Form */}
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="text-sm text-zinc-400 mb-2 block">{t('staking.amountToStake')}</label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={stakeAmount}
                          onChange={(e) => setStakeAmount(e.target.value)}
                          placeholder={`${t('staking.min')}: ${selectedPlan?.minAmount || 0}`}
                          className="h-12 bg-[#133554] border-amber-800/30 text-white pr-20"
                        />
                        <button
                          onClick={() => setStakeAmount(String(user?.tickets || 0))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded"
                        >
                          MAX
                        </button>
                      </div>
                      <p className="text-zinc-500 text-xs mt-1">
                        {t('staking.available')}: {user?.tickets || 0} {getTokenSymbol()}
                      </p>
                    </div>

                    {stakeAmount && selectedPlan && (
                      <div className="bg-[#133554] rounded-xl p-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">{t('staking.lockPeriod')}</span>
                          <span className="text-white">{selectedPlan.duration} {t('staking.days')}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">{t('staking.estReward')}</span>
                          <span className="text-green-400">
                            +{calculateReward(parseFloat(stakeAmount) || 0, selectedPlan.apy, selectedPlan.duration)} {getTokenSymbol()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-400">{t('staking.totalReturn')}</span>
                          <span className="text-white font-bold">
                            {(parseFloat(stakeAmount) || 0) + parseFloat(calculateReward(parseFloat(stakeAmount) || 0, selectedPlan.apy, selectedPlan.duration))} {getTokenSymbol()}
                          </span>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleStake}
                      disabled={staking || !stakeAmount || parseFloat(stakeAmount) < (selectedPlan?.minAmount || 0)}
                      className="w-full h-12 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 disabled:from-zinc-700 disabled:to-zinc-700 text-white font-bold"
                    >
                      {staking ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t('staking.processing')}
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          {t('staking.stakeNow')}
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </DialogContent>
        </Dialog>

        {/* Unstake Dialog */}
        <Dialog open={showUnstakeDialog} onOpenChange={setShowUnstakeDialog}>
          <DialogContent className="bg-[#0a1d30] border-amber-800/50 max-w-sm p-6">
            <h3 className="text-lg font-bold text-white mb-4 text-center">{t('staking.confirmUnstake')}</h3>
            
            {selectedStake && (
              <div className="space-y-4">
                <div className="bg-[#133554] rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-zinc-400 text-sm">{t('staking.stakedAmount')}</span>
                    <span className="text-white font-bold">{selectedStake.amount} {getTokenSymbol()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400 text-sm">{t('staking.estReward')}</span>
                    <span className="text-green-400 font-bold">
                      +{calculateReward(
                        selectedStake.amount,
                        stakingPlans.find(p => p.id === selectedStake.plan_id)?.apy || 5,
                        stakingPlans.find(p => p.id === selectedStake.plan_id)?.duration || 7
                      )} {getTokenSymbol()}
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowUnstakeDialog(false)}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-zinc-400"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleUnstake}
                    disabled={unstaking}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white"
                  >
                    {unstaking ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      t('common.confirm')
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        </div>{/* End of blur wrapper */}
      </div>
    </Layout>
  );
};

export default Staking;
